// Rendu d'une image satellite (canvas) avec les parcelles superposées, pour les fiches PDF.
import { colorForKey } from "./colors";

const TILE = 256;
const tileUrl = (z, x, y) =>
  `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

function project(lat, lng, z) {
  const scale = TILE * 2 ** z;
  const x = ((lng + 180) / 360) * scale;
  const r = (lat * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * scale;
  return [x, y];
}

function loadTile(z, x, y) {
  return new Promise((resolve) => {
    const max = 2 ** z;
    if (y < 0 || y >= max) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = tileUrl(z, ((x % max) + max) % max, y);
  });
}

// Renvoie un dataURL JPEG de la carte, ou null si aucune parcelle localisée.
export async function renderMapImage(parcels, { width = 1200, height = 800, maxZoom = 18 } = {}) {
  const polys = parcels.filter((p) => p.polygon && p.polygon.length >= 3);
  if (!polys.length) return null;

  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  polys.forEach((p) =>
    p.polygon.forEach(([lat, lng]) => {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
    })
  );

  let z = maxZoom;
  for (; z > 3; z--) {
    const [x1, y1] = project(maxLat, minLng, z);
    const [x2, y2] = project(minLat, maxLng, z);
    if (x2 - x1 <= width * 0.8 && y2 - y1 <= height * 0.8) break;
  }

  const [cx, cy] = project((minLat + maxLat) / 2, (minLng + maxLng) / 2, z);
  const left = cx - width / 2;
  const top = cy - height / 2;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#e2e8dd";
  ctx.fillRect(0, 0, width, height);

  const jobs = [];
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx++) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty++) {
      jobs.push(loadTile(z, tx, ty).then((img) => img && ctx.drawImage(img, tx * TILE - left, ty * TILE - top)));
    }
  }
  await Promise.all(jobs);

  polys.forEach((p) => {
    const color = colorForKey(p.crop_label || p.crop || p.owner || p.name);
    ctx.beginPath();
    p.polygon.forEach(([lat, lng], i) => {
      const [x, y] = project(lat, lng, z);
      if (i === 0) ctx.moveTo(x - left, y - top);
      else ctx.lineTo(x - left, y - top);
    });
    ctx.closePath();
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
  });

  ctx.font = "600 17px Arial";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  polys.forEach((p) => {
    let sx = 0, sy = 0;
    p.polygon.forEach(([lat, lng]) => {
      const [x, y] = project(lat, lng, z);
      sx += x;
      sy += y;
    });
    const x = sx / p.polygon.length - left;
    const y = sy / p.polygon.length - top;
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(0,0,0,0.75)";
    ctx.strokeText(p.name, x, y);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(p.name, x, y);
  });

  return canvas.toDataURL("image/jpeg", 0.85);
}
